import { z } from "zod";

import { type JsonQuery, zArrayQuery, zJsonObject, zJsonQuery } from "~/lib/types";

const zRecordQueryBase = z.object({
  id: z.string().optional(),
  properties: zJsonQuery.optional(),
  metadata: zJsonQuery.optional(),
  categoryNames: zArrayQuery.optional(),
  evidenceIDsFilter: z.array(z.string()).optional(),
  withDeactivated: z.boolean().optional(),
});

type RecordQueryBase = z.infer<typeof zRecordQueryBase>;

// NOTE(Tom): To define zRecordQuery with z.lazy(() => zRecordQuery), we need to define the type explicitly.
// Otherwise, it causes a type error saying
// `Function implicitly has return type 'any' because it does not have a return type annotation and is referenced directly or indirectly in one of its return expressions.ts(7024)`
//
// This must be in sync with RecordQueryDTO in cnut.
// TODO(Tom): write a generator to copy the types from cnut to here.
type RecordQuery = RecordQueryBase & {
  AND?: RecordQuery[];
  OR?: RecordQuery[];
};

const zRecordQuery: z.ZodType<RecordQuery> = zRecordQueryBase.extend({
  AND: z.array(z.lazy(() => zRecordQuery)).optional(),
  OR: z.array(z.lazy(() => zRecordQuery)).optional(),
});

type SourceTargetResolverQuery = RecordQuery & {
  evidenceMetadata?: JsonQuery;
  evidenceProperties?: JsonQuery;
};

const zSourceTargetResolverQuery: z.ZodType<SourceTargetResolverQuery> = zRecordQueryBase.extend({
  evidenceMetadata: zJsonQuery.optional(),
  evidenceProperties: zJsonQuery.optional(),
  AND: z.array(z.lazy(() => zSourceTargetResolverQuery)).optional(),
  OR: z.array(z.lazy(() => zSourceTargetResolverQuery)).optional(),
});

// NOTE(Tom): To define zEvidenceQuery with z.lazy(() => zEvidenceQuery), we need to define the type explicitly.
type EvidenceQuery = {
  properties?: JsonQuery;
  metadata?: JsonQuery;
  AND?: EvidenceQuery[];
  OR?: EvidenceQuery[];
};

const zEvidenceQuery: z.ZodType<EvidenceQuery> = z.object({
  properties: zJsonQuery.optional(),
  metadata: zJsonQuery.optional(),
  AND: z.array(z.lazy(() => zEvidenceQuery)).optional(),
  OR: z.array(z.lazy(() => zEvidenceQuery)).optional(),
});

const zRecordResolverOption = z.object({
  skipOnFail: z.boolean().optional(),
});

const zRecordResolver = z
  .object({
    query: zRecordQuery,
    opts: zRecordResolverOption.optional(),
  })
  .passthrough();

const zSourceTargetResolver = z
  .object({
    query: zSourceTargetResolverQuery,
    opts: zRecordResolverOption.optional(),
  })
  .passthrough();

const zRecordMapping = z.object({
  properties: zJsonObject,
  metadata: zJsonObject,
});

export const zEntityComposerDefinition = z
  .object({
    categoryNames: z.array(z.string()),
    entityIdResolver: zRecordResolver,
    evidenceFilter: zEvidenceQuery.optional(),
    entity: zRecordMapping,
    composerKeyExtras: z.array(z.string()).optional(),
  })
  .passthrough();

export const zRelationComposerDefinition = z
  .object({
    categoryNames: z.array(z.string()),
    relationIdResolver: zRecordResolver,
    evidenceFilter: zEvidenceQuery.optional(),
    sourceResolver: zSourceTargetResolver,
    targetResolver: zSourceTargetResolver,
    relation: zRecordMapping,
    composerKeyExtras: z.array(z.string()).optional(),
  })
  .passthrough();
